import type { ReachCalculatedField } from './unsafe-execution'

/**
 * A calculated field as the org endpoint returns it: the domain-level fields for
 * every domain on the account, each tagged with the domain it belongs to. Both
 * casings of the domain key and the timestamp are accepted because the endpoint
 * has been seen to answer in either.
 */
export interface OrgCalculatedField extends ReachCalculatedField {
    domain_id?: number
    domainId?: number
}

/**
 * Reduces the org's per-domain calculated fields to the shared set the unsafe
 * check reads, scoped to the currently selected domains.
 *
 * A calculated field means the same computation only when it matches on name and
 * expression, so the per-domain rows are grouped by that pair. The reduced field
 * counts as computed — a truthy lastCalculatedAt — only when EVERY selected domain
 * carries it and every one of those has recorded a run. A selected domain that
 * lacks the field, or has it but has never computed it, nulls the timestamp: so
 * adding a domain to the selection can turn a safe org segment unsafe.
 */
export function reduceOrgCalculatedFields(
    fields: readonly OrgCalculatedField[],
    selectedDomainIds: readonly number[],
): ReachCalculatedField[] {
    const selected = new Set(selectedDomainIds)

    interface Group {
        name: string
        expression: string
        domainIds: Set<number>
        everyComputed: boolean
        sampleTimestamp: string | null | undefined
    }

    const groups = new Map<string, Group>()

    for (const field of fields) {
        const domainId = field.domain_id ?? field.domainId
        if (domainId === undefined || !selected.has(domainId)) continue

        const key = `${field.name} ${field.expression}`
        const timestamp = field.lastCalculatedAt ?? field.last_calculated_at

        let group = groups.get(key)
        if (!group) {
            group = {
                name: field.name,
                expression: field.expression,
                domainIds: new Set(),
                everyComputed: true,
                sampleTimestamp: undefined,
            }
            groups.set(key, group)
        }

        group.domainIds.add(domainId)
        if (!timestamp) {
            group.everyComputed = false
        } else if (group.sampleTimestamp === undefined) {
            group.sampleTimestamp = timestamp
        }
    }

    const reduced: ReachCalculatedField[] = []
    for (const group of groups.values()) {
        const everyDomainHasField = selectedDomainIds.every((id) => group.domainIds.has(id))
        const computed = group.everyComputed && everyDomainHasField

        reduced.push({
            name: group.name,
            expression: group.expression,
            lastCalculatedAt: computed ? (group.sampleTimestamp ?? null) : null,
        })
    }

    return reduced
}
